// Tipos base del catálogo de órdenes de compra
export interface PurchaseOrder {
  id: string;
  orderNumber: string; // Número de Orden de Compra (ej: PO-PY-1062)
  supplier: string;
  item: string;
  location: string;
  accountingAccount: string;
  class: string;
  trade: string;
  date: Date;
  amountUSD: number;
}

// Tipos para Supply Master (maestro de proveedores)
export interface CustomColumn {
  id: string;
  name: string;
  type: 'text' | 'number' | 'date' | 'select';
  options?: string[]; // Para tipo select
}

export interface SupplierMaster {
  id: string;
  // Campos obligatorios
  razonSocial: string;
  articulo: string; // Artículo principal/categoría
  ordenes: number; // Suma de cantidad de órdenes en historia
  montoTotalUSD: number; // Suma total en USD en historia
  nombreRepresentante: string;
  apellidoRepresentante: string;
  telefonoRepresentante: string;
  emailRepresentante: string;
  
  // Dimensiones del CSV
  cuentasContables: string[];
  clases: string[];
  ubicaciones: string[];
  
  // Fechas
  primeraOrdenFecha: string;
  ultimaOrdenFecha: string;
  
  // Categorías asignadas del Supply Master
  assignedCategories?: string[];
  
  // Columnas customizables (hasta 5)
  customFields?: Record<string, any>;
  
  // Metadatos
  createdAt: Date;
  updatedAt: Date;
}

// Tipos para Proveedores
export interface Supplier {
  id: string;
  name: string;
  status: 'active' | 'potential' | 'inactive';
  tier?: SupplierTier;
  rating?: SupplierRating;
  
  // Datos de contacto (pendiente de recibir catálogo)
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  
  // Datos calculados del historial
  totalPurchases?: number;
  totalAmountUSD?: number;
  categories?: string[];
  locations?: string[];
  firstPurchaseDate?: Date;
  lastPurchaseDate?: Date;
  
  // Metadatos
  createdAt: Date;
  updatedAt: Date;
}

// Tipos para Proveedores Potenciales (CRM)
export type PipelineStage = 
  | 'lead'           // Contacto inicial
  | 'qualified'      // Lead calificado
  | 'proposal'       // Propuesta enviada
  | 'negotiation'    // En negociación
  | 'approved'       // Aprobado
  | 'rejected';      // Rechazado

export interface PotentialSupplier {
  id: string;
  name: string;
  stage: PipelineStage;
  
  // Datos de contacto
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  
  // Datos de prospección
  categories: string[]; // Array de categorías del Supply Master
  notes?: string;
  
  // Fechas del pipeline
  createdAt: Date;
  updatedAt: Date;
  stageChangedAt: Date;
  expectedCloseDate?: Date;
}

// Tipos para Evaluación de Proveedores
export type SupplierTier = 1 | 2 | 3; // 1=Crítico, 2=Importante, 3=Apoyo

export type SupplierRating = 
  | 'strategic'      // Estratégico (4.5-5.0)
  | 'preferred'      // Preferido (3.5-4.49)
  | 'developing'     // En Desarrollo (2.5-3.49)
  | 'conditional'    // Condicional (1.5-2.49)
  | 'disabled';      // Inhabilitado (<1.5)

export interface EvaluationDimension {
  name: string;
  weight: number; // Porcentaje (0-100)
  score: number;  // Puntuación 1-5
  criteria: EvaluationCriterion[];
}

export interface EvaluationCriterion {
  id: string;
  name: string;
  description: string;
  score: number; // 1-5
  weight: number; // Porcentaje dentro de la dimensión
  evidence?: string;
  evaluatedAt?: Date;
  evaluatedBy?: string;
}

export interface SupplierEvaluation {
  id: string;
  supplierId: string;
  supplierName: string;
  tier: SupplierTier;
  
  // Dimensiones de evaluación
  dimensions: {
    quality: EvaluationDimension;         // 30%
    reliability: EvaluationDimension;     // 25%
    competitiveness: EvaluationDimension; // 20%
    safety: EvaluationDimension;          // 15%
    sustainability: EvaluationDimension;  // 10%
  };
  
  // Resultados
  totalScore: number; // 1-5 (weighted average)
  rating: SupplierRating;
  
  // Metadatos
  evaluatedAt: Date;
  evaluatedBy: string;
  validUntil?: Date;
  notes?: string;
}

// Tipos para Ubicaciones
export interface Location {
  id: string;
  name: string;
  type: 'tugboat' | 'office';
}

// Tipos para Reportes y Analytics
export interface SupplierMetrics {
  supplierId: string;
  supplierName: string;
  
  // Métricas financieras
  totalSpend: number;
  averageOrderValue: number;
  orderCount: number;
  
  // Métricas de tiempo
  firstOrderDate: Date;
  lastOrderDate: Date;
  daysSinceLastOrder: number;
  
  // Métricas de categorías
  categoriesCount: number;
  topCategories: { category: string; amount: number }[];
  
  // Métricas de ubicaciones
  locationsCount: number;
  topLocations: { location: string; amount: number }[];
  
  // Métricas de evaluación
  currentTier?: SupplierTier;
  currentRating?: SupplierRating;
  currentScore?: number;
}

export interface CategoryMetrics {
  category: string;
  
  // Métricas financieras
  totalSpend: number;
  orderCount: number;
  supplierCount: number;
  
  // Top proveedores
  topSuppliers: { supplier: string; amount: number }[];
  
  // Concentración
  top1SupplierPercentage: number;
  top3SuppliersPercentage: number;
}

// Tipos para filtros y búsqueda
export interface SupplierFilters {
  search?: string;
  status?: Supplier['status'][];
  tier?: SupplierTier[];
  rating?: SupplierRating[];
  categories?: string[];
  locations?: string[];
  minSpend?: number;
  maxSpend?: number;
  dateFrom?: Date;
  dateTo?: Date;
}

export interface PurchaseOrderFilters {
  search?: string;
  suppliers?: string[];
  categories?: string[];
  locations?: string[];
  accounts?: string[];
  classes?: string[];
  trades?: string[];
  dateFrom?: Date;
  dateTo?: Date;
  minAmount?: number;
  maxAmount?: number;
}

// Tipos para UI/UX
export type ViewMode = 'table' | 'grid' | 'kanban';
export type SortDirection = 'asc' | 'desc';

export interface TableColumn {
  id: string;
  label: string;
  minWidth?: number;
  align?: 'left' | 'center' | 'right';
  sortable?: boolean;
  format?: (value: any) => string;
}

export interface SortConfig {
  field: string;
  direction: SortDirection;
}

// Tipos para navegación
export type NavigationSection = 
  | 'dashboard'
  | 'supply-master'
  | 'active-suppliers'
  | 'potential-suppliers'
  | 'evaluation'
  | 'rfq'
  | 'contracts'
  | 'reception'
  | 'reports';

// Tipos para notas/comentarios
export interface SupplierNote {
  id: string;
  supplierId: string;
  content: string;
  createdBy: string;
  createdAt: Date;
  updatedAt?: Date;
  category?: 'general' | 'commercial' | 'quality' | 'logistics' | 'financial';
}

// ============================================================================
// Tipos para Compulsas & RFQ
// ============================================================================

export type RFQStatus = 'draft' | 'sent' | 'responses' | 'closed';

export interface RFQItem {
  id: string;
  category: string;        // Categoría del Supply Master (item del CSV)
  description: string;     // Descripción detallada de lo que se necesita
  quantity: number;
  unit: string;            // Unidad de medida (kg, litros, unidades, metros, etc.)
  specs?: string;          // Especificaciones técnicas adicionales
}

export interface RFQSupplier {
  supplierId: string;      // ID del proveedor (de dataService)
  supplierName: string;
  contactEmail: string;    // Email de contacto (mock)
  sentAt?: string;         // ISO date cuando se envió
  respondedAt?: string;    // ISO date cuando respondió
  hasResponded: boolean;
}

export interface RFQResponse {
  id: string;
  rfqId: string;
  supplierId: string;
  supplierName: string;
  items: RFQResponseItem[];
  totalAmountUSD: number;
  deliveryDays: number;       // Plazo de entrega en días
  paymentTerms: string;       // Ej: "30 días", "contado", "60 días"
  warranty?: string;          // Ej: "12 meses"
  notes?: string;             // Observaciones del proveedor
  validUntil: string;         // ISO date hasta cuándo es válida la cotización
  submittedAt: string;        // ISO date
}

export interface RFQResponseItem {
  rfqItemId: string;         // Referencia al item del RFQ
  unitPrice: number;
  totalPrice: number;
  brand?: string;            // Marca ofrecida
  origin?: string;           // Origen/procedencia
  notes?: string;
}

export interface RFQ {
  id: string;
  number: string;            // Número correlativo: RFQ-001, RFQ-002...
  title: string;             // Título descriptivo
  status: RFQStatus;
  items: RFQItem[];
  suppliers: RFQSupplier[];  // Proveedores destinatarios
  responses: RFQResponse[];  // Respuestas recibidas
  
  // Metadatos
  createdBy: string;         // "Demo User" para la demo
  createdAt: string;         // ISO date
  sentAt?: string;           // ISO date cuando se envió
  deadline: string;          // ISO date fecha límite para respuestas
  closedAt?: string;         // ISO date cuando se cerró
  notes?: string;            // Notas internas
  
  // Categoría principal (para filtros)
  mainCategory: string;
}

// ============================================================================
// Tipos para Contratos LTP + DocuSign (mock)
// ============================================================================

export type ContractStatus =
  | 'draft'          // Borrador
  | 'review'         // En revisión
  | 'sent_to_sign'   // Enviado a firma (DocuSign mock)
  | 'signed'         // Firmado
  | 'expired'        // Vencido
  | 'cancelled';     // Cancelado

export type ContractType =
  | 'service'        // Contrato de servicio
  | 'supply'         // Contrato de suministro
  | 'framework';     // Contrato marco (acuerdo general)

export type DocuSignStep =
  | 'draft'
  | 'sent'
  | 'viewed'
  | 'signed';

export interface ContractTerm {
  id: string;
  description: string;     // Ej: "Plazo de entrega: 15 días hábiles"
  value: string;            // Ej: "15 días hábiles"
}

export interface ContractClause {
  id: string;
  title: string;            // Ej: "Cláusula de Indemnización"
  content: string;          // Contenido completo de la cláusula
  required: boolean;        // Si es obligatoria o puede quitarse
}

export interface ContractHistoryEntry {
  id: string;
  action: string;           // Ej: "Contrato creado", "Enviado a DocuSign", etc.
  date: string;             // ISO date
  user: string;             // Quién hizo la acción
  details?: string;         // Detalles adicionales
}

export interface Contract {
  id: string;
  contractNumber: string;   // CTR-001, CTR-002...
  title: string;
  supplierId: string;
  supplierName: string;
  type: ContractType;
  status: ContractStatus;
  startDate: string;        // ISO date
  endDate: string;          // ISO date
  value: number;            // Monto del contrato en USD
  currency: string;         // "USD"
  terms: ContractTerm[];
  clauses: ContractClause[];
  history: ContractHistoryEntry[];

  // DocuSign mock
  docusignEnvelopeId?: string;
  docusignStep?: DocuSignStep;
  docusignSentAt?: string;
  docusignViewedAt?: string;
  docusignSignedAt?: string;

  // Metadatos
  createdBy: string;
  createdAt: string;        // ISO date
  updatedAt: string;        // ISO date
  signedAt?: string;        // ISO date
  notes?: string;
}

export interface ContractTemplate {
  clauses: ContractClause[];
  terms: ContractTerm[];
}

// ============================================================================
// Tipos para Wallboard de Recepción (Monitor tipo aeropuerto)
// ============================================================================

export type DeliveryStatus =
  | 'scheduled'    // Programado - todavía no salió
  | 'in_transit'   // En tránsito - en camino
  | 'at_port'      // En puerto - listo para recibir
  | 'receiving'    // Recepcionando - en proceso
  | 'received'     // Recibido - completado
  | 'delayed'      // Demorado - atrasado respecto a ETA
  | 'cancelled';   // Cancelado

export const DELIVERY_STATUS_CONFIG: Record<DeliveryStatus, {
  label: string;
  color: string;
  bgColor: string;
  wallboardColor: string;
  wallboardBg: string;
  icon: string;
  priority: number; // menor = aparece primero en wallboard
}> = {
  delayed: {
    label: 'DEMORADO',
    color: '#d32f2f',
    bgColor: '#ffebee',
    wallboardColor: '#ff1744',
    wallboardBg: 'rgba(255, 23, 68, 0.15)',
    icon: '🔴',
    priority: 0,
  },
  at_port: {
    label: 'EN PUERTO',
    color: '#2e7d32',
    bgColor: '#e8f5e9',
    wallboardColor: '#00e676',
    wallboardBg: 'rgba(0, 230, 118, 0.12)',
    icon: '🟢',
    priority: 1,
  },
  receiving: {
    label: 'RECEPCIONANDO',
    color: '#1565c0',
    bgColor: '#e3f2fd',
    wallboardColor: '#448aff',
    wallboardBg: 'rgba(68, 138, 255, 0.12)',
    icon: '🔵',
    priority: 2,
  },
  in_transit: {
    label: 'ARRIBANDO',
    color: '#e65100',
    bgColor: '#fff3e0',
    wallboardColor: '#ffab00',
    wallboardBg: 'rgba(255, 171, 0, 0.12)',
    icon: '🟡',
    priority: 3,
  },
  scheduled: {
    label: 'PROGRAMADO',
    color: '#616161',
    bgColor: '#f5f5f5',
    wallboardColor: '#90a4ae',
    wallboardBg: 'rgba(144, 164, 174, 0.08)',
    icon: '⚪',
    priority: 4,
  },
  received: {
    label: 'RECIBIDO',
    color: '#00838f',
    bgColor: '#e0f7fa',
    wallboardColor: '#26c6da',
    wallboardBg: 'rgba(38, 198, 218, 0.08)',
    icon: '✅',
    priority: 5,
  },
  cancelled: {
    label: 'CANCELADO',
    color: '#424242',
    bgColor: '#eeeeee',
    wallboardColor: '#616161',
    wallboardBg: 'rgba(97, 97, 97, 0.08)',
    icon: '⚫',
    priority: 6,
  },
};

export interface DeliveryItem {
  id: string;
  description: string;
  category: string;       // Categoría del Supply Master (item del CSV)
  quantity: number;
  unit: string;           // Unidad de medida (kg, litros, unidades, metros, etc.)
  receivedQuantity?: number | null;
}

export interface Delivery {
  id: string;
  supplierId: string;
  supplierName: string;
  locationId: string;
  locationName: string;
  items: DeliveryItem[];
  status: DeliveryStatus;
  estimatedArrival: string;    // ISO datetime
  actualArrival?: string;      // ISO datetime
  notes?: string;
  purchaseOrderRef?: string;   // Referencia a PO
  createdAt: string;           // ISO datetime
  updatedAt: string;           // ISO datetime
}

export type ReceptionStatus = 'conforme' | 'parcial' | 'rechazado';

export interface ReceptionRecord {
  id: string;
  deliveryId: string;
  receivedBy: string;
  receivedAt: string;          // ISO datetime
  status: ReceptionStatus;
  observations?: string;
  itemsReceived?: {
    itemId: string;
    receivedQuantity: number;
  }[];
}

export type NavigationSectionExtended = NavigationSection | 'reception';
